import React, { useState, useEffect, useRef } from 'react';
import Papa from 'papaparse';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { getAuthHeaders } from '../utils/api';
import AthleteHeadshot from './AthleteHeadshot';
import CopyInviteLinkButton from './CopyInviteLinkButton';
import { DEFAULT_ZONE_CHART_COLORS, resolveZoneColor } from '../utils/planAnalytics';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function ClubManagement() {
  const { token, isSuperAdmin, isAdmin, isCoach, user, getEffectiveClubCode } = useAuth();
  const { updateTeamSettings } = useTheme();
  const [athletes, setAthletes] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newTeamName, setNewTeamName] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [trainingLookbackDate, setTrainingLookbackDate] = useState('');
  const [zoneConfigTeam, setZoneConfigTeam] = useState('');
  const [zoneLoggingEnabled, setZoneLoggingEnabled] = useState(false);
  const [zoneDefinitions, setZoneDefinitions] = useState([]);
  const [savingZones, setSavingZones] = useState(false);
  const [trainingPlanEnabled, setTrainingPlanEnabled] = useState(false);
  const [savingTrainingPlanFlag, setSavingTrainingPlanFlag] = useState(false);
  const [targetTimesEnabled, setTargetTimesEnabled] = useState(false);
  const [savingTargetTimesFlag, setSavingTargetTimesFlag] = useState(false);
  const [dragZoneIndex, setDragZoneIndex] = useState(null);
  const zoneRowIdRef = useRef(1);
  
  // GMS state
  const [gmsStandards, setGmsStandards] = useState([]);
  const [gmsBoatClass, setGmsBoatClass] = useState('');
  const [gmsTimeMinutes, setGmsTimeMinutes] = useState('');
  const [gmsTimeSeconds, setGmsTimeSeconds] = useState('');
  const [savingGms, setSavingGms] = useState(false);

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [newInviteEmail, setNewInviteEmail] = useState('');
  const [newInviteTeam, setNewInviteTeam] = useState('');
  const [newInviteRole, setNewInviteRole] = useState('athlete');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(true);

  // Bulk CSV invite state
  const [showCsvInvite, setShowCsvInvite] = useState(false);
  const [csvInvitePreview, setCsvInvitePreview] = useState([]);
  const [csvInviteStatus, setCsvInviteStatus] = useState('');
  const [csvInviteResults, setCsvInviteResults] = useState(null);
  const [uploadingCsvInvites, setUploadingCsvInvites] = useState(false);
  const csvInviteInputRef = useRef(null);
  const [lastCreatedInvite, setLastCreatedInvite] = useState(null);

  // Available boat classes for GMS (gendered for different standards)
  const BOAT_CLASSES = [
    "M Erg", "W Erg",
    "M 1x", "W 1x",
    "M 2x", "W 2x",
    "M 2-", "W 2-",
    "M 4x", "W 4x",
    "M 4-", "W 4-",
    "M 4+", "W 4+",
    "M 8+", "W 8+"
  ];
  const gmsCategoryOptions = [...new Set([
    ...BOAT_CLASSES,
    ...gmsStandards.map((g) => g.boat_class).filter(Boolean)
  ])].sort();

  useEffect(() => {
    fetchAthletes();
    fetchUsers();
    fetchTeamSettings();
    fetchGmsStandards();
    fetchInvitations();
  }, []);

  const withZoneRowIds = (definitions) => {
    return (definitions || []).map(zone => ({
      ...zone,
      _rowId: zone?._rowId || `zone-row-${zoneRowIdRef.current++}`
    }));
  };

  const fetchTeamSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        if (data.settings?.trainingLookbackDate) {
          // Convert from database format to YYYY-MM-DD for input
          const date = new Date(data.settings.trainingLookbackDate);
          setTrainingLookbackDate(date.toISOString().split('T')[0]);
        }
        if (data.settings?.zoneConfigTeam) {
          setZoneConfigTeam(data.settings.zoneConfigTeam);
        }
      }
    } catch (error) {
      console.error('Error fetching team settings:', error);
    }
  };

  const loadZoneSettingsForTeam = async (teamName) => {
    if (!teamName) return;
    try {
      const query = (isAdmin() || isSuperAdmin())
        ? `?team=${encodeURIComponent(teamName)}`
        : '';
      const response = await fetch(`${API_URL}/api/teams/settings${query}`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setZoneLoggingEnabled(Boolean(data?.settings?.zoneLoggingEnabled));
        setTrainingPlanEnabled(Boolean(data?.settings?.trainingPlanEnabled));
        setTargetTimesEnabled(Boolean(data?.settings?.targetTimesEnabled));
        const defs = Array.isArray(data?.settings?.zoneDefinitions) ? data.settings.zoneDefinitions : [];
        setZoneDefinitions(withZoneRowIds(defs));
      } else {
        setZoneLoggingEnabled(false);
        setTrainingPlanEnabled(false);
        setTargetTimesEnabled(false);
        setZoneDefinitions([]);
      }
    } catch (error) {
      console.error('Error loading zone settings:', error);
      setZoneLoggingEnabled(false);
      setTrainingPlanEnabled(false);
      setTargetTimesEnabled(false);
      setZoneDefinitions([]);
    }
  };

  const normalizeZoneKey = (label, fallbackIndex) => {
    const base = String(label || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return base || `zone_${fallbackIndex + 1}`;
  };

  const addZoneDefinition = () => {
    setZoneDefinitions(prev => ([
      ...prev,
      {
        key: `zone_${prev.length + 1}`,
        label: '',
        includeInZonePct: true,
        color: DEFAULT_ZONE_CHART_COLORS[prev.length % DEFAULT_ZONE_CHART_COLORS.length],
        _rowId: `zone-row-${zoneRowIdRef.current++}`
      }
    ]));
  };

  const updateZoneDefinition = (idx, field, value) => {
    setZoneDefinitions(prev => prev.map((zone, i) => i === idx ? { ...zone, [field]: value } : zone));
  };

  const removeZoneDefinition = (idx) => {
    setZoneDefinitions(prev => prev.filter((_, i) => i !== idx));
  };

  const moveZoneDefinition = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    setZoneDefinitions(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };

  const saveZoneSettings = async () => {
    if (!zoneConfigTeam) {
      setMessage('Select a team to configure zone logging');
      return;
    }

    const cleaned = zoneDefinitions
      .map((zone, idx) => {
        const label = String(zone?.label || '').trim();
        if (!label) return null;
        const key = String(zone?.key || '').trim() || normalizeZoneKey(label, idx);
        return {
          key,
          label,
          includeInZonePct: zone?.includeInZonePct !== false,
          color: resolveZoneColor(zone, idx)
        };
      })
      .filter(Boolean);

    if (cleaned.length === 0) {
      setMessage('Add at least one zone label before saving');
      return;
    }

    const canToggleZoneEnablement = isSuperAdmin();
    const payload = {
      zoneLoggingTeam: zoneConfigTeam,
      zoneDefinitions: cleaned
    };
    if (canToggleZoneEnablement) {
      payload.zoneLoggingEnabled = zoneLoggingEnabled;
    }

    setSavingZones(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(token)
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setMessage(`Zone settings saved for ${zoneConfigTeam}`);
        setZoneDefinitions(withZoneRowIds(cleaned));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to save zone settings');
      }
    } catch (error) {
      console.error('Error saving zone settings:', error);
      setMessage('Error saving zone settings');
    } finally {
      setSavingZones(false);
    }
  };

  const saveTrainingPlanFlag = async () => {
    if (!zoneConfigTeam) {
      setMessage('Select a team to configure the training plan feature');
      return;
    }
    setSavingTrainingPlanFlag(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          zoneLoggingTeam: zoneConfigTeam,
          trainingPlanEnabled
        })
      });

      if (response.ok) {
        setMessage(`Training plan setting saved for ${zoneConfigTeam}`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to save training plan setting');
      }
    } catch (error) {
      console.error('Error saving training plan setting:', error);
      setMessage('Error saving training plan setting');
    } finally {
      setSavingTrainingPlanFlag(false);
    }
  };

  const saveTargetTimesFlag = async () => {
    if (!zoneConfigTeam) {
      setMessage('Select a team to configure target times');
      return;
    }
    setSavingTargetTimesFlag(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          zoneLoggingTeam: zoneConfigTeam,
          targetTimesEnabled
        })
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(`Target times setting saved for ${zoneConfigTeam}`);
        if (user?.team && zoneConfigTeam === user.team && data.settings) {
          updateTeamSettings(data.settings);
        }
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(data.error || 'Failed to save target times setting');
      }
    } catch (error) {
      console.error('Error saving target times setting:', error);
      setMessage('Error saving target times setting');
    } finally {
      setSavingTargetTimesFlag(false);
    }
  };

  const saveTrainingLookbackDate = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          trainingLookbackDate: trainingLookbackDate || null
        })
      });

      if (response.ok) {
        setMessage('Lookback date saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to save lookback date');
      }
    } catch (error) {
      console.error('Error saving lookback date:', error);
      setMessage('Error saving lookback date');
    } finally {
      setSaving(false);
    }
  };

  // GMS Functions
  const fetchGmsStandards = async () => {
    try {
      const response = await fetch(`${API_URL}/api/gms`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setGmsStandards(data.standards || []);
      }
    } catch (error) {
      console.error('Error fetching GMS standards:', error);
    }
  };

  const formatTime2k = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  };

  const formatPace = (paceSeconds) => {
    const mins = Math.floor(paceSeconds / 60);
    const secs = (paceSeconds % 60).toFixed(1);
    return `${mins}:${secs.padStart(4, '0')}`;
  };

  const saveGmsStandard = async () => {
    if (!gmsBoatClass || (!gmsTimeMinutes && !gmsTimeSeconds)) {
      setMessage('Please select a boat class and enter a 2k time');
      return;
    }

    const totalSeconds = (parseInt(gmsTimeMinutes) || 0) * 60 + (parseFloat(gmsTimeSeconds) || 0);
    if (totalSeconds < 300 || totalSeconds > 900) {
      setMessage('2k time should be between 5:00 and 15:00');
      return;
    }

    setSavingGms(true);
    try {
      const response = await fetch(`${API_URL}/api/gms`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          boatClass: gmsBoatClass,
          time2kSeconds: totalSeconds
        })
      });

      if (response.ok) {
        setMessage('CST saved successfully!');
        setGmsBoatClass('');
        setGmsTimeMinutes('');
        setGmsTimeSeconds('');
        fetchGmsStandards();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to save CST');
      }
    } catch (error) {
      console.error('Error saving GMS:', error);
      setMessage('Error saving CST');
    } finally {
      setSavingGms(false);
    }
  };

  const deleteGmsStandard = async (boatClass) => {
    if (!confirm(`Delete CST for ${boatClass}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/gms/boat/${encodeURIComponent(boatClass)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        setMessage('CST deleted');
        fetchGmsStandards();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to delete CST');
      }
    } catch (error) {
      console.error('Error deleting GMS:', error);
      setMessage('Error deleting CST');
    }
  };

  const clearLookbackDate = async () => {
    // Clear the state first, then save
    setTrainingLookbackDate('');
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          trainingLookbackDate: null
        })
      });

      if (response.ok) {
        setMessage('Lookback date cleared!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to clear lookback date');
      }
    } catch (error) {
      console.error('Error clearing lookback date:', error);
      setMessage('Error clearing lookback date');
    } finally {
      setSaving(false);
    }
  };

  // Invitation Functions
  const fetchInvitations = async () => {
    try {
      setLoadingInvites(true);
      const response = await fetch(`${API_URL}/api/invitations`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations || []);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    } finally {
      setLoadingInvites(false);
    }
  };

  const sendInvitation = async () => {
    if (!newInviteEmail.trim()) {
      setMessage('Email is required');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setSendingInvite(true);
    try {
      const response = await fetch(`${API_URL}/api/invitations`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          email: newInviteEmail.trim(),
          team: newInviteTeam || null,
          role: newInviteRole
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Existing club members are added to the team directly instead of
        // getting an email invite — surface whichever happened.
        setMessage(data.message || `Invitation sent to ${newInviteEmail}!`);
        if (data.existingMember) {
          setLastCreatedInvite(null);
        } else if (data.invitation?.inviteUrl) {
          setLastCreatedInvite({
            email: data.invitation.email || newInviteEmail.trim(),
            inviteUrl: data.invitation.inviteUrl,
            emailSent: !!data.emailSent
          });
        }
        setNewInviteEmail('');
        setNewInviteTeam('');
        setNewInviteRole('athlete');
        fetchInvitations();
        setTimeout(() => setMessage(''), 5000);
      } else {
        setLastCreatedInvite(null);
        setMessage(data.error || 'Failed to send invitation');
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      setMessage('Error sending invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  const cancelInvitation = async (inviteId) => {
    if (!confirm('Cancel this invitation?')) return;

    try {
      const response = await fetch(`${API_URL}/api/invitations/${inviteId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        setMessage('Invitation cancelled');
        fetchInvitations();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to cancel invitation');
      }
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      setMessage('Error cancelling invitation');
    }
  };

  const resendInvitation = async (inviteId) => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/${inviteId}/resend`, {
        method: 'POST',
        headers: getAuthHeaders(token)
      });

      if (response.ok) {
        setMessage('Invitation resent!');
        fetchInvitations();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to resend invitation');
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      setMessage('Error resending invitation');
    }
  };

  // Bulk CSV invite functions
  const resetCsvInvite = () => {
    setCsvInvitePreview([]);
    setCsvInviteStatus('');
    setCsvInviteResults(null);
    if (csvInviteInputRef.current) {
      csvInviteInputRef.current.value = '';
    }
  };

  const handleCsvInviteFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvInviteStatus('');
    setCsvInviteResults(null);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim().toLowerCase().replace(/[\s_]+/g, ''),
      complete: (results) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const seen = new Set();

        const rows = results.data.map((row, index) => {
          const email = (row.email || row.emailaddress || '').trim();
          const team = (row.teamcode || row.team || '').trim();
          const errors = [];

          if (!email) {
            errors.push('Missing email');
          } else if (!emailRegex.test(email)) {
            errors.push('Invalid email');
          } else if (seen.has(email.toLowerCase())) {
            errors.push('Duplicate in file');
          } else {
            seen.add(email.toLowerCase());
          }

          return {
            row: index + 2, // account for header row and 0-index
            email,
            team,
            errors,
            valid: errors.length === 0
          };
        });

        if (rows.length === 0) {
          setCsvInviteStatus('No rows found in CSV. Make sure it has an "Email" column header.');
          setCsvInvitePreview([]);
          return;
        }

        setCsvInvitePreview(rows);
      },
      error: (error) => {
        console.error('CSV parse error:', error);
        setCsvInviteStatus('Error parsing CSV file. Please check the format.');
      }
    });
  };

  const uploadCsvInvites = async () => {
    const validRows = csvInvitePreview.filter(r => r.valid);
    if (validRows.length === 0) {
      setCsvInviteStatus('No valid rows to invite');
      return;
    }

    setUploadingCsvInvites(true);
    setCsvInviteStatus('');
    try {
      const response = await fetch(`${API_URL}/api/invitations/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          invitations: validRows.map(r => ({ email: r.email, team: r.team || null }))
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCsvInviteResults(data.results);
        setCsvInvitePreview([]);
        if (csvInviteInputRef.current) {
          csvInviteInputRef.current.value = '';
        }
        fetchInvitations();
      } else {
        setCsvInviteStatus(data.error || 'Failed to send invitations');
      }
    } catch (error) {
      console.error('Error uploading CSV invites:', error);
      setCsvInviteStatus('Error sending invitations. Please try again.');
    } finally {
      setUploadingCsvInvites(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/athletes`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setAthletes(data);
        // Extract unique teams
        const fromAthletes = data.flatMap((a) => [
          a.team,
          ...(Array.isArray(a.teams) ? a.teams : [])
        ]).filter(Boolean);
        const nextTeams = [...new Set([...fromAthletes])].sort();
        setTeams((prev) => [...new Set([...prev, ...fromAthletes])].sort());
        if (!zoneConfigTeam && nextTeams.length > 0) {
          const defaultTeam = (isCoach() && user?.team) ? user.team : nextTeams[0];
          setZoneConfigTeam(defaultTeam);
          loadZoneSettingsForTeam(defaultTeam);
        }
      }
    } catch (error) {
      console.error('Error fetching athletes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/athletes/users/club`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        const memberTeams = data.map((u) => u.team).filter(Boolean);
        if (memberTeams.length > 0) {
          setTeams((prev) => [...new Set([...prev, ...memberTeams])].sort());
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const changeMemberRole = async (member, role) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/users/${member.id}/role`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ role })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage(`${member.name} is now a ${role}`);
        setTimeout(() => setMessage(''), 3000);
        fetchUsers();
        fetchAthletes();
      } else {
        setMessage(data.error || 'Failed to update role');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error updating role:', error);
      setMessage('Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const changeMemberTeam = async (member, team) => {
    const previousTeam = member.team || '';
    setUsers((prev) => prev.map((u) => (u.id === member.id ? { ...u, team: team || null } : u)));
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/users/${member.id}/team`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ team: team || null })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setMessage(`${member.name} ${team ? `assigned to ${team}` : 'is now Unassigned'}`);
        setTimeout(() => setMessage(''), 3000);
        fetchUsers();
        fetchAthletes();
      } else {
        setUsers((prev) => prev.map((u) => (u.id === member.id ? { ...u, team: previousTeam || null } : u)));
        setMessage(data.error || 'Failed to update team');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error updating member team:', error);
      setUsers((prev) => prev.map((u) => (u.id === member.id ? { ...u, team: previousTeam || null } : u)));
      setMessage('Failed to update team');
    } finally {
      setSaving(false);
    }
  };

  const getAthletesForTeam = (teamName) => {
    if (teamName === null) {
      return athletes.filter(a => !a.team);
    }
    return athletes.filter(a => a.team === teamName);
  };

  const addTeam = () => {
    if (newTeamName.trim() && !teams.includes(newTeamName.trim())) {
      setTeams([...teams, newTeamName.trim()].sort());
      setNewTeamName('');
      setMessage(`Team "${newTeamName.trim()}" created. Add athletes to populate it.`);
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const updateAthleteTeam = async (athleteId, newTeam) => {
    const athlete = athletes.find(a => a.id === athleteId);
    const previousTeam = athlete?.team || '';
    setAthletes((prev) => prev.map((a) => (
      a.id === athleteId
        ? { ...a, team: newTeam || null, teams: newTeam ? [newTeam] : [] }
        : a
    )));
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}`, {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          name: athlete?.name,
          racingclass: athlete?.racingclass,
          email: athlete?.email,
          phone: athlete?.phone,
          height: athlete?.height,
          weight: athlete?.weight,
          ergScore: athlete?.ergScore || athlete?.erg_score,
          team: newTeam || null
        }),
      });

      if (response.ok) {
        await fetchAthletes();
        await fetchUsers();
        setMessage(`Athlete moved to ${newTeam || 'Unassigned'}`);
        setTimeout(() => setMessage(''), 2000);
      } else {
        const data = await response.json().catch(() => ({}));
        setAthletes((prev) => prev.map((a) => (
          a.id === athleteId
            ? { ...a, team: previousTeam || null, teams: previousTeam ? [previousTeam] : [] }
            : a
        )));
        setMessage(data.error || 'Failed to update athlete');
        setTimeout(() => setMessage(''), 4000);
      }
    } catch (error) {
      console.error('Error updating athlete:', error);
      setAthletes((prev) => prev.map((a) => (
        a.id === athleteId
          ? { ...a, team: previousTeam || null, teams: previousTeam ? [previousTeam] : [] }
          : a
      )));
      setMessage('Error updating athlete');
    } finally {
      setSaving(false);
    }
  };

  const linkAthleteToUser = async (athleteId, userId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/athletes/${athleteId}/link-user`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          userId: userId || null
        }),
      });

      if (response.ok) {
        await fetchAthletes();
        await fetchUsers();
        if (userId) {
          const user = users.find(u => u.id === parseInt(userId));
          setMessage(`Athlete linked to ${user?.name || 'user account'}`);
        } else {
          setMessage('Athlete unlinked from user account');
        }
        setTimeout(() => setMessage(''), 3000);
      } else {
        const data = await response.json();
        setMessage(data.error || 'Failed to link account');
      }
    } catch (error) {
      console.error('Error linking account:', error);
      setMessage('Error linking account');
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = (teamName) => {
    const athletesInTeam = getAthletesForTeam(teamName);
    if (athletesInTeam.length > 0) {
      if (!confirm(`This will unassign ${athletesInTeam.length} athlete(s) from "${teamName}". Continue?`)) {
        return;
      }
      // Unassign all athletes from this team
      athletesInTeam.forEach(athlete => {
        updateAthleteTeam(athlete.id, null);
      });
    }
    setTeams(teams.filter(t => t !== teamName));
    if (selectedTeam === teamName) {
      setSelectedTeam(null);
    }
    setMessage(`Team "${teamName}" deleted`);
    setTimeout(() => setMessage(''), 3000);
  };

  useEffect(() => {
    if (zoneConfigTeam) {
      loadZoneSettingsForTeam(zoneConfigTeam);
    }
  }, [zoneConfigTeam]);

  if (loading) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Club Management</h2>
        <p>Loading...</p>
      </div>
    );
  }

  const unassignedAthletes = getAthletesForTeam(null);

  const messageLooksLikeError = (msg) => {
    if (!msg) return false;
    return (
      msg.includes('Failed') ||
      msg.includes('Error') ||
      msg.includes('already exists') ||
      msg.includes('pending invitation') ||
      msg.includes('Only admins') ||
      msg.includes('Only superadmins')
    );
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Club Management</h2>
      <p className="text-gray-600 mb-6">
        Club-wide settings: create teams, configure rankings and zone logging, manage CST standards,
        and invite new members. For day-to-day roster, squad, and invite management on your own team, use Team Management.
      </p>

      {message && (
        <div className={`mb-4 p-3 rounded ${messageLooksLikeError(message) ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
          {message}
        </div>
      )}

      {/* Ranking Settings */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Ranking Settings</h3>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Training & Attendance Lookback Date
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Set a start date for rankings. Training logs and attendance from this date onward will count toward rankings.
            </p>
            <div className="flex gap-2">
              <input
                type="date"
                value={trainingLookbackDate}
                onChange={(e) => setTrainingLookbackDate(e.target.value)}
                className="border p-2 rounded flex-1 bg-white text-gray-900"
              />
              <button
                onClick={() => saveTrainingLookbackDate()}
                disabled={saving}
                className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              {trainingLookbackDate && (
                <button
                  onClick={() => clearLookbackDate()}
                  disabled={saving}
                  className="bg-gray-200 text-gray-700 px-3 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
                  title="Clear lookback date"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Zone Logging Configuration */}
      {(isCoach() || isAdmin() || isSuperAdmin()) && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold">Zone Logging Configuration</h3>
            {isSuperAdmin() && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                Club: {getEffectiveClubCode?.() || 'Current'}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Configure per-team training zones and enable/disable zone logging. You can define any number of zones
            (for example: UT2, UT1, AT, TR, RP, Power, Mobility). Uncheck “Include in % by zone” for recovery or
            strength so they still log minutes but do not dilute the aerobic-zone mix athletes see.
            Chart color is used on the training-block % by zone bar.
            {isSuperAdmin() && ' Use Club Switcher to manage another club.'}
          </p>
          <p className="text-xs text-gray-500 mb-3">
            Drag rows by the handle to reorder zone display order.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Team</label>
              <select
                value={zoneConfigTeam}
                onChange={(e) => setZoneConfigTeam(e.target.value)}
                className="border p-2 rounded w-full bg-white text-gray-900"
                disabled={isCoach() && !isAdmin() && !isSuperAdmin()}
              >
                <option value="">Select a team...</option>
                {teams.map((team) => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 flex items-end">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={zoneLoggingEnabled}
                  onChange={(e) => setZoneLoggingEnabled(e.target.checked)}
                  disabled={!zoneConfigTeam || !isSuperAdmin()}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">Enable zone logging for this team (superadmin only)</span>
              </label>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wide text-gray-400 px-1">
              <span className="col-span-1" />
              <span className="col-span-3">Label</span>
              <span className="col-span-3">Key</span>
              <span className="col-span-1">Color</span>
              <span className="col-span-2">Include in % by zone</span>
              <span className="col-span-2" />
            </div>
            {zoneDefinitions.map((zone, idx) => (
              <div
                key={zone._rowId || `zone-definition-${idx}`}
                draggable={Boolean(zoneConfigTeam)}
                onDragStart={() => setDragZoneIndex(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragZoneIndex !== null) {
                    moveZoneDefinition(dragZoneIndex, idx);
                  }
                  setDragZoneIndex(null);
                }}
                onDragEnd={() => setDragZoneIndex(null)}
                className={`grid grid-cols-12 gap-2 ${dragZoneIndex === idx ? 'opacity-60' : ''}`}
              >
                <div className="col-span-1 flex items-center justify-center border rounded bg-gray-50 text-gray-500 cursor-move select-none">
                  ::
                </div>
                <input
                  type="text"
                  value={zone.label || ''}
                  onChange={(e) => updateZoneDefinition(idx, 'label', e.target.value)}
                  placeholder="Zone label (e.g., UT2)"
                  className="col-span-12 md:col-span-3 border p-2 rounded bg-white text-gray-900"
                  disabled={!zoneConfigTeam}
                />
                <input
                  type="text"
                  value={zone.key || ''}
                  onChange={(e) => updateZoneDefinition(idx, 'key', e.target.value)}
                  placeholder="Key (optional)"
                  className="col-span-12 md:col-span-3 border p-2 rounded bg-white text-gray-900"
                  disabled={!zoneConfigTeam}
                />
                <div className="col-span-5 md:col-span-1 flex items-center">
                  <input
                    type="color"
                    value={resolveZoneColor(zone, idx)}
                    onChange={(e) => updateZoneDefinition(idx, 'color', e.target.value)}
                    disabled={!zoneConfigTeam}
                    className="w-full h-10 min-h-[40px] rounded cursor-pointer border border-gray-300 bg-white disabled:opacity-50"
                    title="Chart color"
                    aria-label={`Chart color for ${zone.label || `zone ${idx + 1}`}`}
                  />
                </div>
                <label className="col-span-7 md:col-span-2 flex items-center gap-2 text-xs text-gray-700 px-1">
                  <input
                    type="checkbox"
                    checked={zone.includeInZonePct !== false}
                    onChange={(e) => updateZoneDefinition(idx, 'includeInZonePct', e.target.checked)}
                    disabled={!zoneConfigTeam}
                    className="w-4 h-4"
                  />
                  <span className="md:sr-only">Include in % by zone</span>
                </label>
                <button
                  type="button"
                  onClick={() => removeZoneDefinition(idx)}
                  className="col-span-5 md:col-span-2 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                  disabled={!zoneConfigTeam || zoneDefinitions.length <= 1}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={addZoneDefinition}
              className="bg-gray-200 text-gray-700 px-3 py-2 rounded hover:bg-gray-300 disabled:opacity-50"
              disabled={!zoneConfigTeam}
            >
              + Add Zone
            </button>
            <button
              type="button"
              onClick={saveZoneSettings}
              className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
              disabled={savingZones || !zoneConfigTeam}
            >
              {savingZones ? 'Saving...' : 'Save Zone Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Target times (prognostic splits) — per team within this club */}
      {(isAdmin() || isSuperAdmin()) && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-3">Target Times</h3>
          <p className="text-xs text-gray-500 mb-4">
            When enabled, this team can calculate prognostic target splits from 2k goals.
            Athletes see a calculator on Daily Workout; coaches set a stroke rate to suggest a percent,
            then can keep or override that percent on workouts and plans.
            Uses the team selected in Zone Logging Configuration above
            {zoneConfigTeam ? ` (currently: ${zoneConfigTeam})` : ''}.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={targetTimesEnabled}
                onChange={(e) => setTargetTimesEnabled(e.target.checked)}
                disabled={!zoneConfigTeam}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">
                Enable target times for {zoneConfigTeam || 'selected team'}
              </span>
            </label>
            <button
              type="button"
              onClick={saveTargetTimesFlag}
              className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
              disabled={savingTargetTimesFlag || !zoneConfigTeam}
            >
              {savingTargetTimesFlag ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Training Plan Feature Flag (superadmin only) */}
      {isSuperAdmin() && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <h3 className="text-lg font-semibold mb-3">Training Plan Builder</h3>
          <p className="text-xs text-gray-500 mb-4">
            When enabled for a team, coaches can publish multi-week planned training volume and athletes' training
            logs prefill from the plan. Uses the team selected in Zone Logging Configuration above
            {zoneConfigTeam ? ` (currently: ${zoneConfigTeam})` : ''}.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={trainingPlanEnabled}
                onChange={(e) => setTrainingPlanEnabled(e.target.checked)}
                disabled={!zoneConfigTeam}
                className="w-4 h-4"
              />
              <span className="text-sm text-gray-700">
                Enable training plans for {zoneConfigTeam || 'selected team'}
              </span>
            </label>
            <button
              type="button"
              onClick={saveTrainingPlanFlag}
              className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
              disabled={savingTrainingPlanFlag || !zoneConfigTeam}
            >
              {savingTrainingPlanFlag ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Send Invitations */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Invite New Members</h3>
        <p className="text-xs text-gray-500 mb-4">
          Invite people into this club by email. New users create an account from the link.
          Someone who already has a RowCentral login at another club joins here with that same password.
          If email is not configured, copy the invite link from the list below.
        </p>
        
        <div className="flex flex-wrap gap-2 mb-4 items-end">
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">Email Address</label>
            <input
              type="email"
              placeholder="email@example.com"
              value={newInviteEmail}
              onChange={(e) => setNewInviteEmail(e.target.value)}
              className="border p-2 rounded w-full bg-white text-gray-900"
            />
          </div>
          <div className="w-36">
            <label className="block text-xs text-gray-600 mb-1">Team (optional)</label>
            <select
              value={newInviteTeam}
              onChange={(e) => setNewInviteTeam(e.target.value)}
              className="border p-2 rounded w-full bg-white text-gray-900"
            >
              <option value="">No team</option>
              {teams.map((team) => (
                <option key={team} value={team}>{team}</option>
              ))}
            </select>
          </div>
          <div className="w-28">
            <label className="block text-xs text-gray-600 mb-1">Role</label>
            <select
              value={newInviteRole}
              onChange={(e) => setNewInviteRole(e.target.value)}
              className="border p-2 rounded w-full bg-white text-gray-900"
            >
              <option value="athlete">Athlete</option>
              <option value="coxswain">Coxswain</option>
              <option value="coach">Coach</option>
              <option value="admin">Admin</option>
              {isSuperAdmin() && <option value="superadmin">Superadmin</option>}
            </select>
          </div>
          <button
            onClick={sendInvitation}
            disabled={sendingInvite || !newInviteEmail.trim()}
            className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
          >
            {sendingInvite ? 'Sending...' : 'Send Invite'}
          </button>
        </div>

        {lastCreatedInvite && (
          <div className="mb-4 border border-blue-200 bg-blue-50 rounded-lg p-3 text-sm text-blue-900">
            <p className="font-medium">
              Invite ready for {lastCreatedInvite.email}
              {lastCreatedInvite.emailSent ? ' (email marked sent).' : ' (email was not delivered).'}
            </p>
            <p className="text-xs break-all mt-1">{lastCreatedInvite.inviteUrl}</p>
            <CopyInviteLinkButton url={lastCreatedInvite.inviteUrl} className="mt-2 text-blue-700 hover:text-blue-900 text-xs font-semibold" />
          </div>
        )}

        {/* Bulk CSV Invite */}
        <div className="mb-4">
          <button
            onClick={() => {
              setShowCsvInvite(!showCsvInvite);
              if (showCsvInvite) resetCsvInvite();
            }}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {showCsvInvite ? '− Hide bulk invite' : '+ Bulk invite via CSV'}
          </button>

          {showCsvInvite && (
            <div className="mt-3 border rounded-lg p-4 bg-gray-50">
              <p className="text-xs text-gray-600 mb-3">
                Upload a CSV with an <span className="font-semibold">Email</span> column (required) and an
                optional <span className="font-semibold">Team Code</span> column. Everyone is invited as an athlete.
              </p>
              <input
                ref={csvInviteInputRef}
                type="file"
                accept=".csv"
                onChange={handleCsvInviteFileSelect}
                className="text-sm mb-3"
              />

              {csvInviteStatus && (
                <p className="text-sm text-red-600 mb-3">{csvInviteStatus}</p>
              )}

              {csvInvitePreview.length > 0 && (
                <div>
                  <p className="text-sm text-gray-700 mb-2">
                    {csvInvitePreview.filter(r => r.valid).length} of {csvInvitePreview.length} rows ready to invite
                  </p>
                  <div className="overflow-x-auto max-h-64 overflow-y-auto border rounded bg-white mb-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="py-2 px-2 font-medium">Row</th>
                          <th className="py-2 px-2 font-medium">Email</th>
                          <th className="py-2 px-2 font-medium">Team</th>
                          <th className="py-2 px-2 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvInvitePreview.map((row) => (
                          <tr key={row.row} className={`border-b ${row.valid ? '' : 'bg-red-50'}`}>
                            <td className="py-1.5 px-2 text-gray-500">{row.row}</td>
                            <td className="py-1.5 px-2">{row.email || '-'}</td>
                            <td className="py-1.5 px-2">{row.team || '-'}</td>
                            <td className="py-1.5 px-2">
                              {row.valid ? (
                                <span className="text-green-600 text-xs">Ready</span>
                              ) : (
                                <span className="text-red-600 text-xs">{row.errors.join(', ')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={uploadCsvInvites}
                      disabled={uploadingCsvInvites || csvInvitePreview.filter(r => r.valid).length === 0}
                      className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
                    >
                      {uploadingCsvInvites
                        ? 'Sending...'
                        : `Send ${csvInvitePreview.filter(r => r.valid).length} Invite${csvInvitePreview.filter(r => r.valid).length === 1 ? '' : 's'}`}
                    </button>
                    <button
                      onClick={resetCsvInvite}
                      disabled={uploadingCsvInvites}
                      className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {csvInviteResults && (
                <div className="mt-2">
                  <p className="text-sm text-green-700 font-medium">
                    ✅ {csvInviteResults.sent} invitation{csvInviteResults.sent === 1 ? '' : 's'} sent
                    {csvInviteResults.addedExisting > 0 && `, ${csvInviteResults.addedExisting} existing member${csvInviteResults.addedExisting === 1 ? '' : 's'} added to the team`}
                    {csvInviteResults.skipped > 0 && `, ${csvInviteResults.skipped} skipped`}
                  </p>
                  {csvInviteResults.errors?.length > 0 && (
                    <ul className="mt-1 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                      {csvInviteResults.errors.map((err, i) => (
                        <li key={i}>{err.email || '(no email)'}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pending Invitations */}
        <div className="border-t pt-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Pending Invitations ({invitations.filter(i => !i.isExpired).length})
          </h4>
          {loadingInvites ? (
            <p className="text-gray-500 text-sm">Loading invitations...</p>
          ) : invitations.length === 0 ? (
            <p className="text-gray-500 text-sm">No pending invitations.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 font-medium">Email</th>
                    <th className="py-2 font-medium">Team</th>
                    <th className="py-2 font-medium">Role</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invite) => (
                    <tr key={invite.id} className="border-b hover:bg-gray-50">
                      <td className="py-2">{invite.email}</td>
                      <td className="py-2">{invite.team || '-'}</td>
                      <td className="py-2 capitalize">{invite.role}</td>
                      <td className="py-2">
                        {invite.isExpired ? (
                          <span className="text-red-600 text-xs">Expired</span>
                        ) : (
                          <span className="text-green-600 text-xs">Pending</span>
                        )}
                      </td>
                      <td className="py-2 text-right space-x-2">
                        <CopyInviteLinkButton url={invite.inviteUrl} />
                        <button
                          onClick={() => resendInvitation(invite.id)}
                          className="text-blue-600 hover:text-blue-800 text-xs"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => cancelInvitation(invite.id)}
                          className="text-red-600 hover:text-red-800 text-xs"
                        >
                          Cancel
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* GMS (Gold Medal Standard) Settings */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">CST (Gold Medal Standard)</h3>
        <p className="text-xs text-gray-500 mb-4">
          Set 2k reference times for each boat class. % CST in results compares athlete pace to the CST pace.
        </p>
        
        {/* Add new GMS */}
        <div className="flex flex-wrap gap-2 mb-4 items-end">
          <div className="flex-1 min-w-32">
            <label className="block text-xs text-gray-600 mb-1">Boat Class / Category</label>
            <input
              list="gms-category-options"
              value={gmsBoatClass}
              onChange={(e) => setGmsBoatClass(e.target.value)}
              placeholder="e.g., M 8+, 3v8, 2v8"
              className="border p-2 rounded w-full bg-white text-gray-900"
            />
            <datalist id="gms-category-options">
              {gmsCategoryOptions.map((bc) => (
                <option key={bc} value={bc}>{bc}</option>
              ))}
            </datalist>
          </div>
          <div className="w-20">
            <label className="block text-xs text-gray-600 mb-1">Minutes</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="6"
              value={gmsTimeMinutes}
              onChange={(e) => setGmsTimeMinutes(e.target.value.replace(/[^0-9]/g, ''))}
              className="border p-2 rounded w-full bg-white text-gray-900"
            />
          </div>
          <div className="w-20">
            <label className="block text-xs text-gray-600 mb-1">Seconds</label>
            <input
              type="text"
              inputMode="decimal"
              placeholder="30"
              value={gmsTimeSeconds}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/[^0-9.]/g, '');
                const [whole = '', ...fractionParts] = cleaned.split('.');
                const fraction = fractionParts.join('');
                setGmsTimeSeconds(fractionParts.length > 0 ? `${whole}.${fraction}` : whole);
              }}
              className="border p-2 rounded w-full bg-white text-gray-900"
            />
          </div>
          <button
            onClick={saveGmsStandard}
            disabled={savingGms || !gmsBoatClass}
            className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
          >
            {savingGms ? 'Saving...' : 'Add'}
          </button>
        </div>

        {/* Existing GMS standards */}
        {gmsStandards.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 font-medium">Boat Class</th>
                  <th className="py-2 font-medium text-right">2k Time</th>
                  <th className="py-2 font-medium text-right">Pace (/500m)</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {gmsStandards.map((gms) => (
                  <tr key={gms.boat_class} className="border-b hover:bg-gray-50">
                    <td className="py-2 font-medium">{gms.boat_class}</td>
                    <td className="py-2 text-right font-mono">{formatTime2k(gms.time_2k_seconds)}</td>
                    <td className="py-2 text-right font-mono text-gray-600">{formatPace(gms.pace_500m)}</td>
                    <td className="py-2 text-right">
                      <button
                        onClick={() => deleteGmsStandard(gms.boat_class)}
                        className="text-red-600 hover:text-red-800 text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No CST standards set. Add one above to enable % CST tracking in results.</p>
        )}
      </div>

      {/* Create New Team */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h3 className="text-lg font-semibold mb-3">Create New Team</h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Team name (e.g., HPG, Varsity, JV)"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
            className="border p-2 rounded flex-1 bg-white text-gray-900"
            onKeyPress={(e) => e.key === 'Enter' && addTeam()}
          />
          <button
            onClick={addTeam}
            disabled={!newTeamName.trim()}
            className="btn-theme-primary px-4 py-2 rounded disabled:opacity-50"
          >
            Create Team
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teams List */}
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-3">Teams ({teams.length})</h3>
          {teams.length === 0 ? (
            <p className="text-gray-500">No teams created yet. Create a team above.</p>
          ) : (
            <div className="space-y-2">
              {teams.map((team) => {
                const athleteCount = getAthletesForTeam(team).length;
                return (
                  <div
                    key={team}
                    className={`p-3 rounded border cursor-pointer flex justify-between items-center ${
                      selectedTeam === team ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedTeam(selectedTeam === team ? null : team)}
                  >
                    <div>
                      <span className="font-medium">{team}</span>
                      <span className="text-gray-500 text-sm ml-2">({athleteCount} athletes)</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete team "${team}"?`)) {
                          deleteTeam(team);
                        }
                      }}
                      className="text-red-500 hover:text-red-700 px-2"
                      title="Delete team"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Unassigned Athletes */}
          <div className="mt-6 pt-4 border-t">
            <div
              className={`p-3 rounded border cursor-pointer flex justify-between items-center ${
                selectedTeam === 'unassigned' ? 'border-amber-500 bg-amber-50' : 'hover:bg-gray-50'
              }`}
              onClick={() => setSelectedTeam(selectedTeam === 'unassigned' ? null : 'unassigned')}
            >
              <div>
                <span className="font-medium text-amber-700">⚠️ Unassigned Athletes</span>
                <span className="text-gray-500 text-sm ml-2">({unassignedAthletes.length})</span>
              </div>
            </div>
            {unassignedAthletes.length > 0 && (
              <p className="text-xs text-amber-600 mt-2">
                These athletes cannot see workouts, attendance, or roster until assigned to a team.
              </p>
            )}
          </div>
        </div>

        {/* Team Details / Athletes */}
        <div className="bg-white p-4 rounded-lg shadow">
          {selectedTeam ? (
            <>
              <h3 className="text-lg font-semibold mb-3">
                {selectedTeam === 'unassigned' ? 'Unassigned Athletes' : `${selectedTeam} Roster`}
              </h3>
              
              {saving && <p className="text-blue-600 text-sm mb-2">Saving...</p>}

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(selectedTeam === 'unassigned' ? unassignedAthletes : getAthletesForTeam(selectedTeam)).map((athlete) => {
                  const linkedUserId = athlete.user_id || athlete.linked_user_id;
                  const linkedUser = linkedUserId ? users.find(u => u.id === linkedUserId) : null;
                  return (
                    <div key={athlete.id} className="p-3 border rounded space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <AthleteHeadshot name={athlete.name} headshotUrl={athlete.headshot_url} size="sm" />
                          <span className="font-medium">{athlete.name}</span>
                          {linkedUser && (
                            <span className="ml-2 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              ✓ Linked to {linkedUser.name}
                            </span>
                          )}
                          {!linkedUser && athlete.has_user_account && (
                            <span className="ml-2 text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
                              ⚠ Account exists but not linked
                            </span>
                          )}
                        </div>
                        <select
                          value={athlete.team || ''}
                          onChange={(e) => updateAthleteTeam(athlete.id, e.target.value)}
                          className="border p-1 rounded text-sm bg-white ml-2"
                          disabled={saving}
                        >
                          <option value="">Unassigned</option>
                          {teams.map((team) => (
                            <option key={team} value={team}>{team}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-600">Link Account:</label>
                        <select
                          value={linkedUserId || ''}
                          onChange={(e) => linkAthleteToUser(athlete.id, e.target.value)}
                          className="border p-1 rounded text-xs bg-white flex-1"
                          disabled={saving}
                        >
                          <option value="">No account</option>
                          {users.map((user) => (
                            <option key={user.id} value={user.id} disabled={user.athlete_id && user.athlete_id !== athlete.id}>
                              {user.name} ({user.email}) {user.athlete_id && user.athlete_id !== athlete.id ? ' [already linked]' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
                {(selectedTeam === 'unassigned' ? unassignedAthletes : getAthletesForTeam(selectedTeam)).length === 0 && (
                  <p className="text-gray-500 text-center py-4">No athletes in this group</p>
                )}
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p>Select a team to view and manage its roster</p>
            </div>
          )}
        </div>
      </div>

      {/* Club members */}
      {users.length > 0 && (
        <div className="mt-6 bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-3">Club members</h3>
          {saving && <p className="text-blue-600 text-sm mb-2">Saving...</p>}
          <p className="text-xs text-gray-500 mb-3">
            Assign each login to a team in this club. A second-club membership starts Unassigned until you pick a team.
          </p>
          <div className="divide-y">
            {users.map((member) => (
              <div key={member.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="font-medium truncate">{member.name}</p>
                  <p className="text-xs text-gray-500 truncate">{member.email} · {member.role}</p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                <select
                  value={member.team || ''}
                  disabled={saving}
                  onChange={(e) => changeMemberTeam(member, e.target.value)}
                  className="border rounded px-2 py-1 text-xs bg-white text-gray-800"
                  aria-label={`Team for ${member.name}`}
                >
                  <option value="">Unassigned</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>{team}</option>
                  ))}
                </select>
                {(member.role === 'athlete' || member.role === 'coxswain') && (
                  <select
                    value={member.role}
                    disabled={saving}
                    onChange={(e) => changeMemberRole(member, e.target.value)}
                    className="border rounded px-2 py-1 text-xs bg-white text-gray-800"
                  >
                    <option value="athlete">Athlete</option>
                    <option value="coxswain">Coxswain</option>
                  </select>
                )}
                {member.id !== user?.id && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={async () => {
                      if (!window.confirm(`Remove ${member.name} from this club? Their login stays; they will lose access here.`)) {
                        return;
                      }
                      setSaving(true);
                      try {
                        const response = await fetch(
                          `${API_URL}/api/athletes/users/${member.id}/club-membership`,
                          { method: 'DELETE', headers: getAuthHeaders(token) }
                        );
                        const data = await response.json().catch(() => ({}));
                        if (response.ok) {
                          setMessage(`${member.name} removed from club`);
                          setTimeout(() => setMessage(''), 3000);
                          fetchUsers();
                        } else {
                          setMessage(data.error || 'Failed to remove member');
                          setTimeout(() => setMessage(''), 4000);
                        }
                      } catch (error) {
                        console.error('Error removing member:', error);
                        setMessage('Failed to remove member');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="mt-6 bg-white p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-3">Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold">{teams.length}</div>
            <div className="text-sm text-gray-600">Teams</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold">{athletes.length}</div>
            <div className="text-sm text-gray-600">Total Athletes</div>
          </div>
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-2xl font-bold">{athletes.length - unassignedAthletes.length}</div>
            <div className="text-sm text-gray-600">Assigned</div>
          </div>
          <div className="p-3 bg-amber-50 rounded">
            <div className="text-2xl font-bold text-amber-700">{unassignedAthletes.length}</div>
            <div className="text-sm text-amber-600">Unassigned</div>
          </div>
        </div>
      </div>
    </div>
  );
}

